import axios from 'axios';
import Config from '../config/Config';

// Config values
const {
  baseUrl,
  username,
  password,
  serviceRequestNotificationsRoot: srnRoot,
  imageUrlType = 'preview-page', // 'preview-page' or 'direct-image'
} = Config.filebrowserService;

// FileBrowser API base (always /api)
const API_BASE = `${baseUrl}/api`;

// JWT cache
let authToken = null;
let tokenExpiry = null;

// Single axios instance
const apiClient = axios.create({
  baseURL: API_BASE,
});

// Auto-login + attach X-Auth header
apiClient.interceptors.request.use(async (config) => {
  if (!authToken || Date.now() > tokenExpiry) {
    try {
      const res = await axios.post(`${API_BASE}/login`, { username, password });
      authToken = res.data; // raw JWT string
      tokenExpiry = Date.now() + 25 * 60 * 1000; // 25 min (refresh 5 min early)
      console.log('Filebrowser login successful');
    } catch (err) {
      console.error('Filebrowser login failed:', err.response?.data || err.message);
      throw new Error('Filebrowser authentication failed');
    }
  }

  config.headers['X-Auth'] = authToken;
  delete config.headers['Authorization']; // Filebrowser uses X-Auth, not Bearer
  return config;
});

export const ensureReportFolder = async (productId) => {
  const root = srnRoot.startsWith('/') ? srnRoot : `/${srnRoot}`;
  const pathsToCreate = [
    root,
    `${root}/${productId}`,
    `${root}/${productId}/images`,
  ];

  for (const path of pathsToCreate) {
    const folderPath = path.endsWith('/') ? path : `${path}/`;

    try {
      await apiClient.post(`/resources${encodeURIComponent(folderPath)}`, {});
      console.log(`Folder created: ${folderPath}`);
    } catch (err) {
      if (err.response?.status === 409) {
        console.log(`Folder already exists: ${folderPath}`);
        continue;
      }
      console.error(`Failed to create folder ${folderPath}:`, err.response?.data || err.message);
      throw err;
    }
  }
};

export const uploadProblemReportImages = async (files, productId) => {
  if (!files || files.length === 0) {
    throw new Error('No files selected');
  }

  await ensureReportFolder(productId);

  const uploadPath = `${srnRoot}/${productId}/images`.replace(/^\/+/, '/');
  const results = [];

  for (const originalFile of files) {
    const file = new File([originalFile], originalFile.name, {
      type: originalFile.type || 'application/octet-stream',
      lastModified: originalFile.lastModified,
    });

    const destination = `${uploadPath}/${file.name}`;

    try {
      await apiClient.post(
        `/resources${encodeURIComponent(destination)}?override=true`,
        file,
        {
          headers: {
            'Content-Type': file.type || 'application/octet-stream',
          },
          timeout: 30000,
        }
      );

      // Get modified timestamp for cache busting (direct-image mode)
      const metadataRes = await apiClient.get(`/resources${encodeURIComponent(destination)}`);
      const modifiedMs = Date.parse(metadataRes.data.modified);

      let url;
      if (imageUrlType === 'direct-image') {
        url = `${baseUrl}/api/preview/big${destination}?inline=true&key=${modifiedMs}`;
      } else {
        url = `${baseUrl}/files${destination}`;
      }

      results.push({ filename: file.name, url, success: true });
    } catch (err) {
      const msg = err.response?.data?.message || err.message;
      console.error(`Upload failed: ${file.name} → ${msg}`);
      results.push({ filename: file.name, url: null, success: false, error: msg });
    }
  }

  return results;
};

export const fetchImageBlob = async (url) => {
  const relativeUrl = url.replace(baseUrl, '');
  const res = await apiClient.get(relativeUrl, { responseType: 'blob' });
  return res.data;
};

const FilebrowserService = {
  ensureReportFolder,
  uploadProblemReportImages,
  fetchImageBlob,
};

export default FilebrowserService;